import './TaxesTiles.scss';

import { bool, object } from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { some } from 'lodash';

import BeaconDisclosureModal from 'components/shared/DisclosureModal/DisclosureModal';

import { Dollar } from 'components/shared/Text/Text';
import Content from 'services/Content';
import {
  getDefaultTaxYear,
  getRZGainsLossesCurrentYear,
  getRZGainsLossesPriorYear,
  getURZGainsLossesData
} from 'state/taxes/TaxSelectors';
import {
  getHasUnrealizedDataError,
  getHasRealizedCurrentYearDataError,
  getHasRealizedPriorYearDataError
} from 'state/requests/RequestStatusSelectors';
import { CURRENT_YEAR, PRIOR_YEAR } from 'services/Dates';
import { isDesktopLayout, isMobileLayout } from 'state/view/ViewSelectors';
import { AAMBeaconSegmentedControl, AAMBeaconSegmentedControls } from 'components/shared/Beacon';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import lockIcon from 'assets/icons/hex-lock-icon.svg';

const yearOptions = [
  {
    label: PRIOR_YEAR,
    value: PRIOR_YEAR.toString()
  },
  {
    label: CURRENT_YEAR,
    value: CURRENT_YEAR.toString()
  }
];

const TaxSummaryV2 = (props) => {
  const [displayYear, setDisplayYear] = useState(props.defaultTaxYear);

  useEffect(() => {
    setDisplayYear(props.defaultTaxYear);
  }, [props]);

  const changeYear = (year) => {
    setDisplayYear(year);
  };

  const gainLossesStatus = () => {
    const {
      hasUnrealizedDataError,
      hasRealizedDataCurrentError,
      hasRealizedDataPriorError,
      unrealizedGainsLosses,
      realizedGainsLossesCurrent,
      realizedGainsLossesPrior
    } = props;
    return (
      some(
        [
          unrealizedGainsLosses,
          realizedGainsLossesCurrent,
          realizedGainsLossesPrior
        ],
        (value) => some(value, record => record.recordStatus === 'UNKNOWN')
      ) ||
      some(
        [
          hasUnrealizedDataError,
          hasRealizedDataCurrentError,
          hasRealizedDataPriorError
        ],
        (value) => value === true
      )
    );
  };

  const { summary } = props;

  const yearToUse = summary[displayYear];

  const controlsRef = useRef(null);
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.addEventListener('beacon-event-input', (event) => {
        const currentValue = event.detail.value;
        changeYear(currentValue);
      });
    }

    return () => {
      if (controlsRef.current) {
        controlsRef.current.removeEventListener('beacon-event-input', (event) => {
          const currentValue = event.detail.value;
          changeYear(currentValue);
        });
      }
    };
  }, []);

  return (
    <div className="tile-summary-v2">
      <div className="tile-summary-content-v2 beacon-on-light">
        <div className="tile-summary-header-v2 b-font-weight-bold b-typography-sans-serif--headline-loose--100" slot='title'>
          Tax summary
          <AAMBeaconSegmentedControls scale="mini"
                                      className="tile-summary-toggle-bar-v2"
                                      ref={controlsRef}>
            <p slot="title" className='b-visually-hidden' aria-label={"hidden select year title"}>Select Year</p>
            <AAMBeaconSegmentedControl selected={displayYear === yearOptions[0].value}>
              {yearOptions[0].label}
            </AAMBeaconSegmentedControl>
            <AAMBeaconSegmentedControl selected={displayYear === yearOptions[1].value}>
              {yearOptions[1].label}
            </AAMBeaconSegmentedControl>
          </AAMBeaconSegmentedControls>
          <div className={"display-year-v2 b-font-weight-bold b-typography-sans-serif--body-loose--200"}>{displayYear}</div>
        </div>
        <div className="tile-summary-rows">
          {gainLossesStatus() ? (
            <div className="cost-basis-section-error-v2">
              <div className="cost-basis-section-error-lock-icon-container-v2">
                <div className="cost-basis-error-content">
                  <p className="b-typography-sans-serif--body-loose--200 b-font-weight-bold ">
                    <img src={lockIcon} alt="lock icon" />
                    Realized gain/loss cannot be retrieved.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="tile-summary-row tile-summary-row-text">
                <span className="b-font-weight-bold b-typography-sans-serif--body--100">Realized gain/loss</span>
                <Dollar className="b-font-weight-bold b-typography-sans-serif--body--100">
                  {yearToUse.realizedGainLossTotal}
                </Dollar>
              </div>
              <div className="tile-summary-row tile-summary-row-text">
                <span className="b-font-weight-light b-typography-sans-serif--body--100">Short-term</span>
                <Dollar className="b-font-weight-light b-typography-sans-serif--body--100">
                  {yearToUse.realizedGainLossShortTerm}
                </Dollar>
              </div>
              <div className="tile-summary-row tile-summary-row-text">
                <span className="b-font-weight-light b-typography-sans-serif--body--100">Long-term</span>
                <Dollar className="b-font-weight-light b-typography-sans-serif--body--100">
                  {yearToUse.realizedGainLossLongTerm}
                </Dollar>
              </div>
            </>
          )}
          <div className="tile-summary-row-border">
            <div className="tile-summary-row tile-summary-row-text">
                <span className="b-font-weight-bold b-typography-sans-serif--body--100">
                  Capital gain distribution
                </span>
              <Dollar className="b-font-weight-bold b-typography-sans-serif--body--100">
                {yearToUse.capitalGainDistributionTotal}
              </Dollar>
            </div>
            <div className="tile-summary-row tile-summary-row-text">
              <span className="b-font-weight-light b-typography-sans-serif--body--100">Short-term</span>
              <Dollar className="b-font-weight-light b-typography-sans-serif--body--100">
                {yearToUse.capitalGainDistributionShortTerm}
              </Dollar>
            </div>
            <div className="tile-summary-row tile-summary-row-text">
              <span className="b-font-weight-light b-typography-sans-serif--body--100">Long-term</span>
              <Dollar className="b-font-weight-light b-typography-sans-serif--body--100">
                {yearToUse.capitalGainDistributionLongTerm}
              </Dollar>
            </div>
          </div>
          <div className="tile-summary-row tile-summary-row-text">
            <span className="b-font-weight-bold b-typography-sans-serif--body--100">Income dividend</span>
            <Dollar className="b-font-weight-bold b-typography-sans-serif--body--100">{yearToUse.incomeDividend}</Dollar>
          </div>
        </div>
        <div className="tile-text-summary-footer-v2">
          <BeaconDisclosureModal
            linkContent={'Important Information'}
            closeOnClickOut={true}
            size={'Normal'}>
            <Fragment>
              <div className="b-typography-serif--headline--200">Important Information</div>
              <div className="b-typography-sans-serif--body--200 b-font-weight-light">
                <Disclosure content={Content.taxSummaryDisclosureV2} />
              </div>
            </Fragment>
          </BeaconDisclosureModal>
        </div>
      </div>
    </div>
)};


TaxSummaryV2.propTypes = {
  isDesktopLayout: bool.isRequired,
  isMobileLayout: bool.isRequired,
  summary: object.isRequired
};

const mapStateToProps = (state) => ({
  defaultTaxYear: getDefaultTaxYear(state),
  isDesktopLayout: isDesktopLayout(state),
  isMobileLayout: isMobileLayout(state),
  unrealizedGainsLosses: getURZGainsLossesData(state),
  hasUnrealizedDataError: getHasUnrealizedDataError(state),
  realizedGainsLossesCurrent: getRZGainsLossesCurrentYear(state),
  hasRealizedDataCurrentError: getHasRealizedCurrentYearDataError(state),
  realizedGainsLossesPrior: getRZGainsLossesPriorYear(state),
  hasRealizedDataPriorError: getHasRealizedPriorYearDataError(state)
});

export default connect(mapStateToProps)(TaxSummaryV2);
